import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useAuth } from "@/context/AuthContext";
import { ChatCircle, X, PaperPlaneTilt, ArrowLeft, User, Circle, Paperclip, FilePdf, FileImage, File, DownloadSimple } from "@phosphor-icons/react";
import { fmtTime } from "@/lib/fmt";

/**
 * Floating in-app chat widget (bottom-right on every authed page).
 * - Peer list scoped by role: carrier sees only their assigned dispatcher; internal roles see internal + assigned carriers.
 * - Polls unread count every 8s, active thread every 4s while open.
 */
const POLL_UNREAD_MS = 8000;
const POLL_MESSAGES_MS = 4000;

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // list | thread
  const [threads, setThreads] = useState([]);
  const [peers, setPeers] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [totalUnread, setTotalUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null); // File object (pre-upload)
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  const authed = !!(user && user.id);

  const fetchUnread = useCallback(async () => {
    if (!authed) return;
    try {
      const { data } = await api.get("/chat/unread-count");
      setTotalUnread(data.count || 0);
    } catch { /* silent */ }
  }, [authed]);

  const fetchThreads = useCallback(async () => {
    if (!authed) return;
    try {
      const [{ data: t }, { data: p }] = await Promise.all([
        api.get("/chat/threads"),
        api.get("/chat/peers"),
      ]);
      setThreads(t);
      setPeers(p);
    } catch { /* silent */ }
  }, [authed]);

  const fetchMessages = useCallback(async (thread) => {
    if (!thread) return;
    try {
      const { data } = await api.get(`/chat/threads/${thread.id}/messages`);
      setMessages(data);
      await api.post(`/chat/threads/${thread.id}/read`).catch(() => {});
      fetchUnread();
    } catch { /* silent */ }
  }, [fetchUnread]);

  // Global unread polling
  useEffect(() => {
    if (!authed) return;
    fetchUnread();
    const t = setInterval(fetchUnread, POLL_UNREAD_MS);
    return () => clearInterval(t);
  }, [authed, fetchUnread]);

  // Load thread list when widget opens
  useEffect(() => {
    if (open && view === "list") fetchThreads();
  }, [open, view, fetchThreads]);

  // Poll active thread messages while open
  useEffect(() => {
    if (!open || view !== "thread" || !activeThread) return;
    fetchMessages(activeThread);
    const t = setInterval(() => fetchMessages(activeThread), POLL_MESSAGES_MS);
    return () => clearInterval(t);
  }, [open, view, activeThread, fetchMessages]);

  // Autoscroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const openThread = async (thread) => {
    setActiveThread(thread);
    setView("thread");
  };

  const startWithPeer = async (peer) => {
    try {
      const { data: thread } = await api.post("/chat/threads/start", { peer_id: peer.id });
      thread.peer = peer;
      thread.unread_count = 0;
      setActiveThread(thread);
      setView("thread");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start chat"); }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if ((!text && !attachment) || !activeThread || sending) return;
    setSending(true);

    // If there's a file, upload first
    let file_id = null;
    let attachedMeta = null;
    if (attachment) {
      setUploadingAttachment(true);
      try {
        const fd = new FormData();
        fd.append("file", attachment);
        fd.append("purpose", "chat");
        fd.append("doc_type", "chat_attachment");
        const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_id = up.id;
        attachedMeta = { id: file_id, filename: attachment.name, content_type: attachment.type };
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
        setSending(false);
        setUploadingAttachment(false);
        return;
      }
      setUploadingAttachment(false);
    }

    // Clear input immediately for snappy feel
    setDraft("");
    const wasAttachment = attachment;
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    // Optimistic append
    const tempId = "tmp-" + Date.now();
    setMessages((m) => [...m, {
      id: tempId, from_user_id: user.id, from_name: user.name,
      body: text, file: attachedMeta, created_at: new Date().toISOString(),
    }]);
    try {
      const payload = { body: text };
      if (file_id) payload.file_id = file_id;
      const { data: msg } = await api.post(`/chat/threads/${activeThread.id}/messages`, payload);
      setMessages((m) => m.filter((x) => x.id !== tempId).concat(msg));
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== tempId));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
      setDraft(text);
      setAttachment(wasAttachment);
    } finally { setSending(false); }
  };

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("File too large (max 20 MB)");
      e.target.value = "";
      return;
    }
    setAttachment(f);
  };

  if (!authed) return null;

  // Peers who don't yet have a thread
  const threadPeerIds = new Set(threads.map((t) => t.peer?.id));
  const availablePeers = peers.filter((p) => !threadPeerIds.has(p.id));

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-widget-open"
          className="mfg-float fixed bottom-5 right-4 lg:right-5 z-40 w-14 h-14 rounded-full bg-[#0A84FF] hover:bg-[#0058B0] text-white shadow-2xl grid place-items-center transition-transform hover:scale-105"
          aria-label="Open chat">
          <ChatCircle size={26} weight="fill" />
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center border-2 border-white" data-testid="chat-widget-badge">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="mfg-float fixed bottom-2 inset-x-2 sm:inset-x-auto sm:bottom-5 sm:right-5 z-40 sm:w-[380px] h-[78vh] sm:h-[560px] max-h-[calc(100vh-40px)] bg-white rounded-2xl shadow-2xl border border-black/10 flex flex-col overflow-hidden animate-in slide-in-from-bottom-2 duration-200"
          data-testid="chat-widget-panel">
          {/* Header */}
          <div className="bg-black text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              {view === "thread" && (
                <button onClick={() => { setView("list"); setActiveThread(null); }} data-testid="chat-back"
                  className="p-1 hover:bg-white/10 rounded"><ArrowLeft size={16} weight="bold" /></button>
              )}
              <ChatCircle size={18} weight="fill" className="text-[#0A84FF]" />
              <div>
                <div className="font-display font-bold text-sm">
                  {view === "list" ? "Messages" : activeThread?.peer?.name || "Chat"}
                </div>
                {view === "thread" && activeThread?.peer?.role && (
                  <div className="text-[10px] text-white/50 capitalize">{activeThread.peer.role.replace("_", " ")}</div>
                )}
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close"
              className="p-1 hover:bg-white/10 rounded"><X size={16} weight="bold" /></button>
          </div>

          {/* Body */}
          {view === "list" ? (
            <div className="flex-1 overflow-y-auto">
              {threads.length === 0 && availablePeers.length === 0 ? (
                <div className="p-6 text-center text-sm text-neutral-500">
                  {user.role === "carrier" ? "No dispatcher assigned yet — ask an admin." : "No teammates available."}
                </div>
              ) : (
                <>
                  {threads.map((t) => (
                    <button key={t.id} onClick={() => openThread(t)} data-testid={`chat-thread-${t.id}`}
                      className="w-full text-left p-3 hover:bg-neutral-50 border-b border-black/5 flex items-start gap-3">
                      <PeerAvatar peer={t.peer} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm text-black truncate">{t.peer?.name || "—"}</div>
                          {t.unread_count > 0 && (
                            <span className="bg-[#0A84FF] text-white text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] rounded-full grid place-items-center">{t.unread_count}</span>
                          )}
                        </div>
                        <div className="text-xs text-neutral-500 truncate">{t.last_message_preview || "No messages yet"}</div>
                      </div>
                    </button>
                  ))}
                  {availablePeers.length > 0 && (
                    <>
                      <div className="px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-neutral-400 bg-neutral-50">Start a new chat</div>
                      {availablePeers.map((p) => (
                        <button key={p.id} onClick={() => startWithPeer(p)} data-testid={`chat-start-${p.id}`}
                          className="w-full text-left p-3 hover:bg-neutral-50 border-b border-black/5 flex items-center gap-3">
                          <PeerAvatar peer={p} />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm text-black truncate">{p.name}</div>
                            <div className="text-xs text-neutral-500 truncate capitalize">{p.role?.replace("_", " ")}</div>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 bg-neutral-50 flex flex-col gap-2" data-testid="chat-messages">
                {messages.length === 0 && (
                  <div className="text-center text-xs text-neutral-400 py-8">Say hi 👋</div>
                )}
                {messages.map((m) => {
                  const mine = m.from_user_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-[#0A84FF] text-white rounded-br-sm" : "bg-white border border-black/5 text-black rounded-bl-sm"}`}>
                        {m.file && <MessageAttachment file={m.file} mine={mine} />}
                        {m.body && <div className={m.file ? "mt-2" : ""}>{m.body}</div>}
                        <div className={`text-[9px] mt-1 ${mine ? "text-white/60" : "text-neutral-400"}`}>
                          {fmtTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="border-t border-black/5 p-2 shrink-0 bg-white">
                {attachment && (
                  <div className="mb-2 px-2 py-1.5 bg-[#0A84FF]/5 border border-[#0A84FF]/20 rounded-lg flex items-center gap-2 text-xs" data-testid="chat-attachment-preview">
                    <Paperclip size={12} weight="bold" className="text-[#0A84FF]" />
                    <span className="flex-1 truncate text-black font-semibold">{attachment.name}</span>
                    <span className="text-neutral-500 text-[10px]">{(attachment.size / 1024).toFixed(0)} KB</span>
                    <button type="button" onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                      className="text-neutral-400 hover:text-red-600" data-testid="chat-attachment-remove">
                      <X size={12} weight="bold" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2 items-end">
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingAttachment}
                    data-testid="chat-attach-btn"
                    title="Attach photo or PDF (BOL, damage report, etc.)"
                    className="w-9 h-9 rounded-full hover:bg-neutral-100 text-neutral-500 hover:text-[#0A84FF] grid place-items-center shrink-0 disabled:opacity-40">
                    <Paperclip size={16} weight="bold" />
                  </button>
                  <input ref={fileInputRef} type="file" hidden accept="image/*,application/pdf,.doc,.docx,.xlsx" onChange={onFilePick}
                    data-testid="chat-file-input" />
                  <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    data-testid="chat-input"
                    placeholder={attachment ? "Add a caption (optional)…" : "Type a message… (Enter to send)"}
                    rows={1}
                    className="flex-1 resize-none border border-black/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0A84FF] max-h-24" />
                  <button type="submit" disabled={(!draft.trim() && !attachment) || sending} data-testid="chat-send"
                    className="w-9 h-9 rounded-full bg-[#0A84FF] hover:bg-[#0058B0] text-white grid place-items-center disabled:opacity-40 shrink-0">
                    <PaperPlaneTilt size={14} weight="fill" />
                  </button>
                </div>
                {uploadingAttachment && (
                  <div className="mt-1 text-[10px] text-neutral-500 text-center">Uploading attachment…</div>
                )}
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}

function PeerAvatar({ peer }) {
  const initials = (peer?.name || peer?.email || "?").slice(0, 2).toUpperCase();
  const isCarrier = peer?.role === "carrier";
  return (
    <div className={`w-9 h-9 rounded-full ${isCarrier ? "bg-[#0A84FF]" : "bg-black"} text-white grid place-items-center font-bold text-xs shrink-0`}>
      {initials}
    </div>
  );
}

function MessageAttachment({ file, mine }) {
  const isImage = (file.content_type || "").startsWith("image/");
  const isPdf = (file.content_type || "").includes("pdf");
  const url = `/api/files/${file.id}`;
  const iconCls = mine ? "text-white/90" : "text-[#0A84FF]";
  const captionCls = mine ? "text-white/80" : "text-neutral-600";

  if (isImage) {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid={`chat-attachment-${file.id}`}
        className="block -mx-1 -mt-1 mb-1 rounded-xl overflow-hidden max-w-[240px]">
        <img src={url} alt={file.filename} className="w-full h-auto max-h-56 object-cover" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`chat-attachment-${file.id}`}
      className={`flex items-center gap-2 py-1.5 px-2 rounded-lg ${mine ? "bg-white/10 hover:bg-white/20" : "bg-neutral-100 hover:bg-neutral-200"} transition-colors`}>
      {isPdf ? <FilePdf size={20} weight="fill" className={iconCls} /> : <File size={20} weight="fill" className={iconCls} />}
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold truncate">{file.filename}</div>
        <div className={`text-[9px] ${captionCls}`}>{isPdf ? "PDF" : "File"} · click to open</div>
      </div>
      <DownloadSimple size={12} weight="bold" className={iconCls} />
    </a>
  );
}
